import { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Menu,
  MenuButton,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MessageBar,
  MessageBarBody,
  Textarea,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import {
  ChatSparkleRegular,
  DismissRegular,
  DocumentTextRegular,
  FlashRegular,
  PenSparkleRegular,
  WarningRegular,
} from '@fluentui/react-icons';

import { ControlledTooltip } from '@shared/components/ControlledTooltip';
import { useAutosizedTextareaBox } from '@shared/hooks';
import { TERMINOLOGY } from '@shared/i18n';

import { getAiOlderVersionMessage } from '../constants';
import { pickRandomPlaceholderQuestion } from '../groundedQa/helpers';
import type { TGroundedQaRunResponse } from '../groundedQa/types';
import { EAiMode, type TAiAssistantStatusMessage } from '../types';
import AiAssistantDisclaimer from './AiAssistantDisclaimer';
import AiAssistantProgressMessage from './AiAssistantProgressMessage';
import useAiOpenVersion from '../hooks/useAiOpenVersion.hook';
import { useBottomBarStyles } from '../hooks/useBottomBarStyles.hook';

const useStyles = makeStyles({
  inputBox: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '2px',
      backgroundColor: tokens.colorCompoundBrandStroke,
      transform: 'scaleX(0)',
      transition: `transform ${tokens.durationUltraFast} ${tokens.curveAccelerateMid}`,
      pointerEvents: 'none',
    },
    '&:focus-within::after': {
      transform: 'scaleX(1)',
      transition: `transform ${tokens.durationNormal} ${tokens.curveDecelerateMid}`,
    },
  },
  textarea: {
    flex: '1 1 auto',
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    '&::before, &::after': { display: 'none' },
    '& textarea': {
      height: '100%',
      maxHeight: '100%',
      overflowY: 'auto',
      paddingBottom: `calc(${tokens.spacingVerticalXXL} + ${tokens.spacingVerticalS})`,
      scrollPaddingBottom: `calc(${tokens.spacingVerticalXXL} + ${tokens.spacingVerticalS})`,
    },
  },
  footerStack: { position: 'relative', flexShrink: 0 },
  selectedContent: {
    position: 'absolute',
    top: 0,
    left: tokens.spacingHorizontalMNudge,
    zIndex: 1,
    transform: `translateY(calc(-100% - ${tokens.spacingVerticalS}))`,
  },
  bottomRow: {
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
  },
  modeMenuPopover: { width: '280px', maxWidth: 'calc(100vw - 32px)' },
});

type TAiModeOption = {
  label: string;
  description: string;
};

const AI_MODE_OPTIONS: Record<EAiMode, TAiModeOption> = {
  [EAiMode.STANDARD]: {
    label: 'Standard',
    description: 'Fast responses for everyday tasks.',
  },
  [EAiMode.ADVANCED]: {
    label: 'Advanced',
    description: 'Better results for complex tasks. Takes longer and uses more credits.',
  },
};
const AI_MODES = Object.values(EAiMode);

type TProps = {
  value: string;
  onChange: (value: string) => void;
  onSubmitAsk?: () => void;
  onSubmitEdit?: () => void;
  isAskPending: boolean;
  isEditPending: boolean;
  aiMode: EAiMode;
  isAiModeChangeDisabled: boolean;
  onAiModeChange: (aiMode: EAiMode) => void;
  isContentSelected: boolean;
  canSubmitAsk: boolean;
  canSubmitEdit: boolean;
  // False when the current document cannot be edited. Edit then renders secondary and disabled,
  // and Ask becomes primary.
  isEditAvailable: boolean;
  editDisabledReason: string | undefined;
  statusMessage?: TAiAssistantStatusMessage | null;
  pendingRun?: TGroundedQaRunResponse | null;
  editProgressMessage?: string;
  onCancel?: () => void;
  canCancel?: boolean;
};

const AiAssistantPromptView = ({
  value,
  onChange,
  onSubmitAsk,
  onSubmitEdit,
  isAskPending,
  isEditPending,
  aiMode,
  isAiModeChangeDisabled,
  onAiModeChange,
  isContentSelected,
  canSubmitAsk,
  canSubmitEdit,
  isEditAvailable,
  editDisabledReason,
  statusMessage,
  pendingRun,
  editProgressMessage,
  onCancel,
  canCancel,
}: TProps) => {
  const [placeholder] = useState(pickRandomPlaceholderQuestion);
  const { containerRef, boxRef, bottomRowRef, textareaRef } = useAutosizedTextareaBox(value);
  const isPending = isAskPending || isEditPending;
  const { hasDraft, isNotLatestVersionOpen } = useAiOpenVersion();
  const aiModeLabel = AI_MODE_OPTIONS[aiMode].label;

  useEffect(() => {
    const raf = requestAnimationFrame(() => textareaRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, []);

  const bottomBarClasses = useBottomBarStyles();
  const classes = useStyles();

  return (
    <div ref={containerRef} className="px-4 pt-3 pb-3 flex flex-col gap-3 h-full min-h-0">
      {isNotLatestVersionOpen && !isEditPending && (
        <MessageBar intent="warning" layout="multiline">
          <MessageBarBody>{getAiOlderVersionMessage(hasDraft)}</MessageBarBody>
        </MessageBar>
      )}
      {statusMessage && (
        <MessageBar intent={statusMessage.intent} layout="multiline">
          <MessageBarBody>{statusMessage.text}</MessageBarBody>
        </MessageBar>
      )}
      <div ref={boxRef} className={classes.inputBox}>
        <Textarea
          ref={textareaRef}
          className={classes.textarea}
          appearance="filled-lighter"
          value={value}
          onChange={(_, data) => onChange(data.value)}
          placeholder={placeholder}
          readOnly={isPending}
          resize="none"
        />
        <div ref={bottomRowRef} className={classes.footerStack}>
          {isContentSelected && (
            <div className={classes.selectedContent}>
              {isNotLatestVersionOpen ? (
                <Badge appearance="tint" color="warning" icon={<WarningRegular />} className="!font-normal">
                  Selection not used
                </Badge>
              ) : (
                <Badge appearance="tint" color="informative" icon={<DocumentTextRegular />} className="!font-normal">
                  Using selected content
                </Badge>
              )}
            </div>
          )}
          <div className={mergeClasses(bottomBarClasses.bar, classes.bottomRow)}>
            <Menu positioning="above-start" checkedValues={{ aiMode: [aiMode] }}>
              <MenuTrigger disableButtonEnhancement>
                <MenuButton
                  appearance="subtle"
                  size="small"
                  icon={<FlashRegular />}
                  menuIcon={null}
                  disabled={isAiModeChangeDisabled}
                  aria-label={`Mode: ${aiModeLabel}`}
                >
                  {aiModeLabel}
                </MenuButton>
              </MenuTrigger>
              <MenuPopover
                className={classes.modeMenuPopover}
                // The popover is portaled outside the overlay DOM, so keep its clicks inside the assistant interaction.
                onMouseDown={(event) => event.stopPropagation()}
              >
                <MenuList>
                  {AI_MODES.map((mode) => (
                    <MenuItemRadio
                      key={mode}
                      name="aiMode"
                      value={mode}
                      subText={AI_MODE_OPTIONS[mode].description}
                      onClick={() => onAiModeChange(mode)}
                    >
                      {AI_MODE_OPTIONS[mode].label}
                    </MenuItemRadio>
                  ))}
                </MenuList>
              </MenuPopover>
            </Menu>
            <div className="flex gap-2">
              {isPending && onCancel ? (
                <Button
                  appearance="secondary"
                  size="small"
                  icon={<DismissRegular />}
                  disabled={!canCancel}
                  onClick={onCancel}
                >
                  Cancel
                </Button>
              ) : (
                <>
                  {onSubmitAsk && (
                    <Button
                      appearance={onSubmitEdit && isEditAvailable ? 'secondary' : 'primary'}
                      size="small"
                      icon={<ChatSparkleRegular />}
                      disabled={!canSubmitAsk}
                      onClick={onSubmitAsk}
                    >
                      {TERMINOLOGY.askButton.upper}
                    </Button>
                  )}
                  {onSubmitEdit && (
                    <ControlledTooltip
                      content={editDisabledReason ?? ''}
                      enable={Boolean(editDisabledReason)}
                      relationship="description"
                      positioning="above"
                      withArrow
                    >
                      <Button
                        appearance={isEditAvailable ? 'primary' : 'secondary'}
                        size="small"
                        icon={<PenSparkleRegular />}
                        disabled={!editDisabledReason && !canSubmitEdit}
                        // A plain disabled button ignores hover, so the tooltip could never open.
                        disabledFocusable={Boolean(editDisabledReason)}
                        onClick={onSubmitEdit}
                      >
                        {TERMINOLOGY.editButton.upper}
                      </Button>
                    </ControlledTooltip>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {isPending ? (
        <div className="shrink-0 flex items-center gap-3">
          <AiAssistantProgressMessage
            progressMessage={isEditPending ? editProgressMessage : pendingRun?.progressMessage}
          />
        </div>
      ) : (
        <AiAssistantDisclaimer />
      )}
    </div>
  );
};

export default AiAssistantPromptView;
